import { initContract } from "@ts-rest/core";
import z from "zod";

import { clinicSchema } from "@libs/db-schemas";
import {
  adHocSelfPaySchema,
  agingIntoMedicareSchema,
  getShortCodePathParamsSchema,
  lapseSchema,
  qrCodeSchema,
  recurringWorkRequirementSchema,
  redeterminationSchema,
  selfPayConversionSchema,
  serviceIntroductionSchema,
} from "@libs/kubb";

import { HTTPStatusCode } from "../../types";
import { errorSchema } from "../../utils/errors";

// Extra fields added to every entry point variant
const extraFields = {
  clinicName: clinicSchema.shape.name,
  clinicLogoBase64: clinicSchema.shape.logoBase64,
  code: z.string(),
};

// Discriminated union keyed by entryPointType — each variant enforces
// exactly the required fields for that entry point type.
export const shortCodeResponseSchema = z.discriminatedUnion("entryPointType", [
  serviceIntroductionSchema.extend(extraFields),
  lapseSchema.extend(extraFields),
  selfPayConversionSchema.extend(extraFields),
  qrCodeSchema.extend(extraFields),
  agingIntoMedicareSchema.extend(extraFields),
  recurringWorkRequirementSchema.extend({
    ...extraFields,
    // From the state's work requirement rules; absent when the state has none.
    actionCampaignThresholdDays: z.number().int().optional(),
  }),
  redeterminationSchema.extend(extraFields),
  adHocSelfPaySchema.extend(extraFields),
]);

const c = initContract();

export const verifyApiContract = c.router({
  getShortCodeData: {
    summary: "Get short code data",
    method: "GET",
    path: "/verify-api/:code",
    pathParams: getShortCodePathParamsSchema,
    responses: {
      // Made nullable to allow for the case where the code is valid but already in use, so we don't expose that fact to the client.
      [HTTPStatusCode.OK]: shortCodeResponseSchema.nullable(),
      [HTTPStatusCode.NOT_FOUND]: errorSchema,
      [HTTPStatusCode.INTERNAL_SERVER_ERROR]: errorSchema,
    },
  },
});
